// Simple recursive camelCase converter
function toCamel(obj: any): any {
    if (Array.isArray(obj)) return obj.map(toCamel);
    if (obj === null || typeof obj !== "object") return obj;

    return Object.keys(obj).reduce((acc, key) => {
        const camelKey = key.replace(/([-_][a-z])/g, (group) =>
            group.toUpperCase().replace("-", "").replace("_", "")
        );
        acc[camelKey] = toCamel(obj[key]);
        return acc;
    }, {} as any);
}

// Simple recursive snake_case converter
function toSnake(obj: any): any {
    if (Array.isArray(obj)) return obj.map(toSnake);
    if (obj === null || typeof obj !== "object") return obj;

    return Object.keys(obj).reduce((acc, key) => {
        const snakeKey = key.replace(/[A-Z]/g, (letter) => `_${letter.toLowerCase()}`);
        acc[snakeKey] = toSnake(obj[key]);
        return acc;
    }, {} as any);
}

const BACKEND_URL = process.env.REACT_APP_BACKEND_BASE || "http://localhost:8080";

// ApiError carries the HTTP status alongside a user-appropriate message. The
// backend writes its {"error": "..."} bodies for end users (see the Go
// respondError helper), so ApiError.message is safe to show directly.
export class ApiError extends Error {
    status: number;
    constructor(message: string, status: number) {
        super(message);
        this.name = "ApiError";
        this.status = status;
    }
}

// getErrorMessage picks the message to show a user for a caught error. Backend
// messages (ApiError) are already written for end users; anything else — bugs,
// unexpected exceptions — falls back to the caller's context-specific default.
export function getErrorMessage(err: unknown, fallback: string): string {
    if (err instanceof ApiError && err.message) return err.message;
    return fallback;
}

// Shown when the backend returns an error without a readable message.
const STATUS_FALLBACKS: Record<number, string> = {
    400: "We couldn't process that request. Please refresh the page and try again.",
    401: "Your session has expired. Please log in again.",
    403: "You don't have permission to do that.",
    404: "That item could not be found. It may have been deleted.",
    429: "You're doing that too quickly. Please wait a moment and try again.",
};

function statusFallback(status: number): string {
    return STATUS_FALLBACKS[status] ?? "Something went wrong on our end. Please try again.";
}

// Endpoints with no session to refresh, or where refreshing would be wrong:
// login, the Google sign-in callback and the second-factor step run before any
// session cookie exists,
// /auth/refresh is the refresh call itself (guards against a loop), and
// /public/ endpoints (password-protected data rooms) authenticate some other
// way entirely. /user/me is deliberately NOT here — it's the most common
// authenticated call (fired on every route change), so a 401 there should
// still get a silent-refresh attempt like any other request.
// Backend message when present, else the generic text for the status.
async function apiErrorFromResponse(response: Response): Promise<ApiError> {
    const errorData = await response.json().catch(() => null);
    const backendMessage =
        typeof errorData?.error === "string"
            ? errorData.error
            : typeof errorData?.error?.message === "string"
                ? errorData.error.message
                : null;
    return new ApiError(backendMessage || statusFallback(response.status), response.status);
}

function isRefreshExemptEndpoint(endpoint: string): boolean {
    return (
        endpoint.includes("/login") ||
        endpoint.includes("/auth/oauth/callback") ||
        endpoint.includes("/auth/mfa/") ||
        endpoint.includes("/auth/refresh") ||
        endpoint.includes("/public/")
    );
}

// Endpoints where a 401 (after a refresh attempt, if one was tried) is an
// expected outcome to show/handle inline rather than a hard redirect: the
// /user/me verification check (AuthContext already renders its own
// session-expired UI for this), /public endpoints, login itself, the Google
// sign-in callback, and the second-factor step.
function isRedirectExemptEndpoint(endpoint: string): boolean {
    return (
        endpoint.includes("/user/") ||
        endpoint.includes("/login") ||
        endpoint.includes("/auth/oauth/callback") ||
        endpoint.includes("/auth/mfa/") ||
        endpoint.includes("/public/")
    );
}

// Shared across calls so concurrent 401s trigger one refresh, not one per
// in-flight request.
let refreshPromise: Promise<boolean> | null = null;

function refreshSession(): Promise<boolean> {
    if (!refreshPromise) {
        refreshPromise = fetch(`${BACKEND_URL}/api/auth/refresh`, {
            method: "POST",
            credentials: "include",
        })
            .then((res) => res.ok)
            .catch(() => false)
            .finally(() => {
                refreshPromise = null;
            });
    }
    return refreshPromise;
}

// Resolves with the OK response unread.
async function requestWithSession(endpoint: string, options: RequestInit, isRetry = false): Promise<Response> {
    const isFormData = options.body instanceof FormData;

    const headers: Record<string, string> = { ...options.headers as Record<string, string> };
    if (!isFormData) {
        headers["Content-Type"] = "application/json";
    }

    let body = options.body;
    if (options.body && typeof options.body === "string") {
        const data = JSON.parse(options.body);
        body = JSON.stringify(toSnake(data));
    }

    // Make the request
    let response: Response;
    try {
        response = await fetch(`${BACKEND_URL}/api${endpoint}`, {
            ...options,
            headers,
            body,
            // CRITICAL: This tells the browser to automatically include the
            // HttpOnly auth cookie that your Go backend set during login!
            credentials: "include",
        });
    } catch (err) {
        // An abort is not a connectivity failure.
        if (err instanceof Error && err.name === "AbortError") throw err;
        // fetch itself only rejects on network-level failures
        throw new ApiError(
            "We couldn't reach the server. Please check your internet connection and try again.",
            0,
        );
    }

    if (!response.ok) {
        // A 401 on an ordinary request usually just means the access token
        // expired — try the refresh token once before giving up on the
        // session. Exempt endpoints (login, /user/me, mfa, public, and the
        // refresh call itself) skip this: a wrong code or a stale check
        // there is a form error to show in place, not a session problem.
        if (response.status === 401 && !isRetry && !isRefreshExemptEndpoint(endpoint)) {
            const refreshed = await refreshSession();
            if (refreshed) {
                return requestWithSession(endpoint, options, true);
            }
        }

        if (response.status === 401 && !isRedirectExemptEndpoint(endpoint)) {
            window.location.href = "/login";
        }

        throw await apiErrorFromResponse(response);
    }

    return response;
}

export async function fetchAPI(endpoint: string, options: RequestInit = {}): Promise<any> {
    const response = await requestWithSession(endpoint, options);

    // Return raw response for 204 No Content
    if (response.status === 204) return null;

    const data = await response.json()

    return toCamel(data);
}

// For server-sent events: same request path as fetchAPI, response not parsed or case-converted.
export function fetchAPIStream(endpoint: string, options: RequestInit = {}): Promise<Response> {
    return requestWithSession(endpoint, options);
}

export async function apiHealth() {
    try {
        const res = await fetch(`${BACKEND_URL}/health`)
        return res.ok
    } catch {
        return false
    }
}